import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { companyInfo, features } from '../mock';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-red-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Welcome to <span className="text-[#02029E]">March Global</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
              {companyInfo.tagline}
            </p>
            <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto leading-relaxed">
              Connecting global markets with premium food products and industrial packaging solutions since {companyInfo.founded}
            </p>
            <Link
              to="/products"
              className="inline-flex items-center px-8 py-4 bg-[#02029E] text-white font-semibold rounded-lg hover:bg-[#02029E]/90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Explore Our Products
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Product Divisions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Product Divisions</h2>
            <p className="text-xl text-gray-600">Two specialized divisions serving global markets</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Food Products Division */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="absolute inset-0 bg-gradient-to-br from-[#02029E]/90 to-[#02029E]/70 z-10"></div>
              <img 
                src="https://images.unsplash.com/photo-1596099477998-880bc06e09f9?w=800" 
                alt="Food Products" 
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 z-20 p-8 flex flex-col justify-end">
                <h3 className="text-3xl font-bold text-white mb-3">Food Products</h3>
                <p className="text-blue-100 mb-6 leading-relaxed">
                  Premium olives and olive oil from our trusted partner factory in Gimlek, Bursa. Experience the authentic taste of Mediterranean quality.
                </p>
                <Link
                  to="/products"
                  className="inline-flex items-center text-white font-semibold hover:text-blue-200 transition-colors group"
                >
                  Learn More
                  <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Industrial Packaging Division */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="absolute inset-0 bg-gradient-to-br from-[#D41414]/90 to-[#D41414]/70 z-10"></div>
              <img 
                src="https://images.unsplash.com/photo-1697350175813-ce77a4c5abc3?w=800" 
                alt="Industrial Packaging" 
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 z-20 p-8 flex flex-col justify-end">
                <h3 className="text-3xl font-bold text-white mb-3">Industrial Packaging</h3>
                <p className="text-red-100 mb-6 leading-relaxed">
                  Comprehensive range of industrial packaging materials including shrink wrap, protection films, and adhesive solutions from global sources.
                </p>
                <Link
                  to="/products?tab=industrial"
                  className="inline-flex items-center text-white font-semibold hover:text-red-200 transition-colors group"
                >
                  Learn More
                  <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose March Global</h2>
            <p className="text-xl text-gray-600">Your trusted partner in global trading</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-[#02029E]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#02029E] to-[#D41414]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Trading?</h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            Get in touch with us today to discuss your requirements and discover how March Global can support your business.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#02029E] font-semibold rounded-lg hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            Contact Us Today
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
